import { useEffect, useState } from 'react';
import { Mic, Square } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { transcribeAudio } from '@/lib/api/interview';
import type { FriendlyError } from '@/lib/errors/types';
import { getAudioError } from '@/lib/errors/audioErrors';
import { getPracticeTranscribeError } from '@/lib/errors/practiceErrors';
import { logger } from '@/lib/logger';
import { captureFriendlyError } from '@/lib/sentry';

const BAR_COUNT = 11;
const IDLE_HEIGHTS = [22, 38, 30, 55, 42, 60, 42, 55, 30, 38, 22];

interface PracticeVoiceInputProps {
  onTranscript: (text: string, confidence?: number) => void;
  onError: (err: FriendlyError) => void;
  questionKeywords: string[];
  disabled: boolean;
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export default function PracticeVoiceInput({
  onTranscript,
  onError,
  questionKeywords,
  disabled,
}: PracticeVoiceInputProps) {
  const {
    status,
    durationMs,
    startRecording,
    stopRecording,
    error: recorderError,
    clearError,
  } = useAudioRecorder();
  const [isProcessing, setIsProcessing] = useState(false);
  const [tickHeights, setTickHeights] = useState<number[]>(IDLE_HEIGHTS);

  const isRecording = status === 'recording';
  const isStopping = status === 'stopping';
  const isBusy = isProcessing || isStopping;

  useEffect(() => {
    if (!isRecording) {
      setTickHeights(IDLE_HEIGHTS);
      return;
    }
    const id = window.setInterval(() => {
      setTickHeights(
        Array.from({ length: BAR_COUNT }, () => 18 + Math.random() * 70),
      );
    }, 130);
    return () => window.clearInterval(id);
  }, [isRecording]);

  useEffect(() => {
    if (!recorderError) return;
    if (recorderError === 'permission_denied') {
      onError(getAudioError('permission_denied'));
    } else if (recorderError === 'recording_too_short') {
      onError(getAudioError('recording_too_short'));
    } else {
      onError(getAudioError('recording_failed'));
    }
    clearError();
  }, [recorderError, onError, clearError]);

  const handlePress = async () => {
    if (disabled || isBusy) return;

    if (isRecording) {
      const blob = await stopRecording();
      if (!blob) return;
      setIsProcessing(true);
      try {
        const result = await transcribeAudio(blob, questionKeywords);
        if (result.success && result.transcript) {
          onTranscript(result.transcript, result.confidence);
        } else {
          const code = result.error?.code ?? 'transcription_failed';
          const friendly = getPracticeTranscribeError(code);
          const context = { apiCode: code, keywordCount: questionKeywords.length };
          logger.error('Practice transcription failed', result.error?.raw, context);
          captureFriendlyError(result.error?.raw, friendly, context);
          onError(friendly);
        }
      } finally {
        setIsProcessing(false);
      }
    } else {
      try {
        await startRecording();
      } catch {
        onError(getAudioError('recording_failed'));
      }
    }
  };

  const helperText = isBusy
    ? 'Processing…'
    : isRecording
      ? 'Listening…'
      : 'Tap to record your answer';

  return (
    <div className="flex flex-col items-center gap-3 w-full">
      <div
        className={`relative w-full h-[88px] rounded-2xl flex items-center gap-4 px-4 md:px-5 transition-colors ${
          isRecording
            ? 'bg-error/10 ring-1 ring-error/30'
            : 'bg-cream-warm/60 ring-1 ring-border/60'
        }`}
      >
        <button
          type="button"
          onClick={handlePress}
          disabled={disabled || isBusy}
          aria-label={isRecording ? 'Stop recording' : 'Start recording'}
          className={`flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
            isRecording
              ? 'bg-error text-white shadow-md hover:bg-error/90'
              : 'bg-primary text-white shadow-sm hover:bg-primary-dark'
          }`}
        >
          {isRecording ? (
            <Square size={18} fill="currentColor" />
          ) : (
            <Mic size={22} />
          )}
        </button>

        <div
          aria-hidden
          className="flex-1 flex items-center justify-center gap-[5px] h-12 overflow-hidden"
        >
          {tickHeights.map((h, i) => (
            <motion.span
              key={i}
              animate={{ height: `${h}%` }}
              transition={{
                duration: 0.13,
                ease: 'easeOut',
              }}
              className={`w-[3px] rounded-full ${
                isRecording ? 'bg-error/70' : 'bg-primary/35'
              }`}
              style={{ minHeight: 4 }}
            />
          ))}
        </div>

        {isRecording && (
          <span className="font-mono text-sm font-semibold text-error tabular-nums flex-shrink-0">
            {formatDuration(durationMs)}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {isRecording && (
          <span className="w-1.5 h-1.5 rounded-full bg-error animate-pulse" />
        )}
        <span
          className={`font-sans text-xs font-medium ${
            isRecording
              ? 'text-error'
              : isBusy
                ? 'text-text-secondary'
                : 'text-text-muted'
          }`}
        >
          {helperText}
        </span>
      </div>
    </div>
  );
}
